// @ts-strict-ignore
import { Line } from './line';
import { OverlapInfo } from './overlap-info';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Matrix4, Vector3 } from 'three';
import { ArticleUtils } from '@article/util/article-utils';
import { CommonConstants } from '../constants/common-constants';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';

export class AdvancedLine extends Line {
  private _overlapInfo: OverlapInfo = null;

  public static createInstanceByLine(line: Line): AdvancedLine {
    return new AdvancedLine(line.start, line.end);
  }

  public static createInstanceByArticle(article: ArticleVO, startPoint: Vector3 = null, endPoint: Vector3 = null): AdvancedLine {
    return new AdvancedLine(startPoint, endPoint,
      new OverlapInfo(ArticleUtils.getArticleOverlapTypes(article),
        ArticleUtils.getArticleOverlapLeft(article), ArticleUtils.getArticleOverlapRight(article)));
  }

  public static calculateMaxOverlapLength(leftLine: AdvancedLine, rightLine: AdvancedLine): number {
    let overlapLength: number = 0;
    if (this.isOverlapEnabled(leftLine, rightLine)) {
      overlapLength = this.calculateOverlapValue(leftLine.overlapRight, rightLine.overlapLeft);
    }
    return overlapLength;
  }

  public static calculateMaxOverlapLengthInInnerLine(line: AdvancedLine, innerLine: AdvancedLine,
                                                     isCalculationForStartLine: boolean = true): number {
    let overlapLength: number = 0;
    if (line && innerLine) {
      if (isCalculationForStartLine) {
        if (this.isTheSameOverlapTypes(line.overlapLeftTypes, innerLine.overlapLeftTypes)) {
          overlapLength = this.calculateOverlapValue(line.overlapLeft, innerLine.overlapLeft);
        }
      } else {
        if (this.isTheSameOverlapTypes(line.overlapRightTypes, innerLine.overlapRightTypes)) {
          overlapLength = this.calculateOverlapValue(line.overlapRight, innerLine.overlapRight);
        }
      }
    }
    return overlapLength;
  }

  private static calculateOverlapValue(firstOverlap: number, secondOverlap: number): number {
    let overlapValue: number = 0;
    if (!NumberUtils.isNumber(firstOverlap) || firstOverlap === 0) {
      overlapValue = secondOverlap;
    } else if (!NumberUtils.isNumber(secondOverlap) || secondOverlap === 0) {
      overlapValue = firstOverlap;
    } else {
      overlapValue = Math.min(firstOverlap, secondOverlap);
    }
    return NumberUtils.isNumber(overlapValue) ? overlapValue : 0;
  }

  public static isOverlapEnabled(leftLine: AdvancedLine, rightLine: AdvancedLine): boolean {
    if (!leftLine || !rightLine) {
      return false;
    }
    return this.isTheSameOverlapTypes(leftLine.overlapRightTypes, rightLine.overlapLeftTypes);
  }

  private static isTheSameOverlapTypes(firstTypes: string[], secondTypes: string[]): boolean {
    return firstTypes && secondTypes && firstTypes.length > 0 && secondTypes.length > 0
      && ArrayUtils.bothArraysContainTheSameElements(firstTypes, secondTypes);
  }

  public constructor(p1: Vector3, p2: Vector3, overlapInfo: OverlapInfo = null) {
    super(p1, p2);
    this._overlapInfo = overlapInfo ? overlapInfo : new OverlapInfo();
  }

  public get overlapInfo(): OverlapInfo {
    return this._overlapInfo;
  }

  public set overlapInfo(overlapInfo: OverlapInfo) {
    this._overlapInfo = overlapInfo;
  }

  public get overlapTypes(): string[] {
    return this._overlapInfo.overlapTypes;
  }

  public get overlapLeftTypes(): string[] {
    return this._overlapInfo.overlapLeftTypes;
  }

  public get overlapRightTypes(): string[] {
    return this._overlapInfo.overlapRightTypes;
  }

  public get overlapLeft(): number {
    return this._overlapInfo.overlapLeft;
  }

  public get overlapRight(): number {
    return this._overlapInfo.overlapRight;
  }

  public split(point: Vector3): Line[] {
    return [new AdvancedLine(this.start, point), new AdvancedLine(point, this.end)];
  }

  public move(delta: Vector3): Line {
    const line: Line = super.move(delta);
    return new AdvancedLine(line.start, line.end, this.overlapInfo);
  }

  public transform(translationMatrix: Matrix4): Line {
    const line: Line = super.transform(translationMatrix);
    return new AdvancedLine(line.start, line.end, this.overlapInfo);
  }

  public reverse(): Line {
    const line: Line = super.reverse();
    const overlapInfo: OverlapInfo = this.overlapInfo.reverse();
    return new AdvancedLine(line.start, line.end, overlapInfo);
  }

  public reverseOverlapProperties(): void {
    const tmpTypes: string[] = this._overlapInfo.overlapRightTypes;
    this._overlapInfo.overlapRightTypes = this.overlapLeftTypes;
    this._overlapInfo.overlapLeftTypes = tmpTypes;
    const tmpOverlap: number = this._overlapInfo.overlapRight;
    this._overlapInfo.overlapRight = this._overlapInfo.overlapLeft;
    this._overlapInfo.overlapLeft = tmpOverlap;
  }

  public hasLine(line: Line, fullInclusion: boolean = true): boolean {
    if (line instanceof AdvancedLine && AdvancedLine.isOverlapEnabled(this, (line as AdvancedLine))) {
      const startOverlap: number = AdvancedLine.calculateMaxOverlapLength(this, line as AdvancedLine);
      const endOverlap: number = AdvancedLine.calculateMaxOverlapLength(line as AdvancedLine, this);
      return fullInclusion ?
        this.hasPointWithOverlap(line.start, 0, startOverlap) && this.hasPointWithOverlap(line.end, endOverlap, 0) :
        this.hasPointWithOverlap(line.start, 0, startOverlap) || this.hasPointWithOverlap(line.end, endOverlap, 0);
    } else {
      return super.hasLine(line, fullInclusion);
    }
  }

  public hasInnerOverlappedLine(line: AdvancedLine, fullInclusion: boolean = true): boolean {
    if (!line) {
      return false;
    }
    const startOverlap: number = AdvancedLine.calculateMaxOverlapLengthInInnerLine(this, line);
    const endOverlap: number = AdvancedLine.calculateMaxOverlapLengthInInnerLine(this, line, false);
    return fullInclusion ?
      this.hasPointWithOverlap(line.start, startOverlap, startOverlap) && this.hasPointWithOverlap(line.end, endOverlap, endOverlap) :
      this.hasPointWithOverlap(line.start, startOverlap, startOverlap) || this.hasPointWithOverlap(line.end, endOverlap, endOverlap);
  }

  public hasPointWithOverlap(point: Vector3, startOverlapDistance: number, endOverlapDistance: number): boolean {
    const sD: number = this.start.distanceTo(point) - startOverlapDistance;
    const eD: number = this.end.distanceTo(point) - endOverlapDistance;
    const d: number = this.length + CommonConstants.EPSILON * 2;
    return (sD <= d && eD <= d);
  }

  public clone(): AdvancedLine {
    return new AdvancedLine(this.start.clone(), this.end.clone(), this.overlapInfo.clone());
  }
}
